import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErrorCode,
  isStaleWrite,
  type FinanceSettingsDto,
  type SaveFinanceSettingsRequest,
  type ComplianceProfileDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

// Re-export types for consumer convenience
export type { FinanceSettingsDto, SaveFinanceSettingsRequest, ComplianceProfileDto };

// ============================================================================
// Query Hooks
// ============================================================================

export function useFinanceSettings() {
  const { selectedCampaign } = useCampaignStore();
  // GET finance/settings requires Finance Manage; a view-only user would only collect a 403.
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));

  return useQuery({
    queryKey: queryKeys.finance.settings(),
    queryFn: async () => {
      const response = await apiResponse('/api/finance/settings');
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch finance settings');
      return response.json() as Promise<FinanceSettingsDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && canManageFinance,
    staleTime: FINANCE_STALE_TIME,
  });
}


export function useComplianceProfile() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.complianceProfile(),
    queryFn: async () => {
      const response = await apiResponse('/api/finance/compliance-profile');
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch compliance profile');
      return response.json() as Promise<ComplianceProfileDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useSaveFinanceSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveFinanceSettingsRequest) =>
      apiJson<FinanceSettingsDto>('/api/finance/settings', {
        method: 'POST',
        json: data,
        error: (body, response) =>
          response.status === 409
            ? Object.assign(
                new Error('These settings were updated elsewhere. Your changes are still here; save again to apply them.'),
                { code: ErrorCode.ConcurrencyConflict },
              )
            : new Error(body.error || 'Failed to save finance settings'),
      }),
    onError: (error) => {
      // A stale token: refetch so the form can adopt the fresh one and the retry is not another 409.
      if (isStaleWrite(error))
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.settings() });
    },
    onSuccess: (saved) => {
      // The form re-initialises from this cache right after a save, before any refetch lands, so it
      // must already hold the fresh concurrency token or the next save is a false 409.
      queryClient.setQueryData(queryKeys.finance.settings(), saved);
      queryClient.invalidateQueries(financeQueries);
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.keyDates() });
    },
  });
}
